import { isNil } from '@/utils/isNil';
import { clsx } from 'clsx';
import { FC, ReactNode } from 'react';

import { getInputErrorId, getInputNoteId } from '../Input.helpers';
import { InputBase, InputBaseWithOptions, InputOptionType } from '../Input.type';
import styles from './FieldWrapper.module.scss';

interface Props extends InputBase<any> {
  children: ReactNode;
  buttonRight?: ReactNode;
  errorTestId?: string;
}

interface GroupProps extends InputBaseWithOptions<any> {
  type: InputOptionType;
  children: ReactNode;
  /** Hides the error message display */
  hideError?: boolean;
  /** Removes bottom margin from the wrapper */
  noMarginBottom?: boolean;
}

export const FieldWrapper: FC<Props> = ({
  id,
  label,
  subLabel,
  value,
  error,
  disabled,
  note,
  className,
  size = 'default',
  children,
  labelOutside = false,
  iconLeft,
  iconRight,
  buttonRight,
  errorTestId,
}) => (
  <div className={clsx(styles.root, 'wrapper', className)}>
    <label
      className={clsx(
        styles.wrapper,
        styles[size],
        {
          [styles.filled]: !!value,
          [styles.error]: !!error,
          [styles.disabled]: disabled,
          [styles.noLabel]: isNil(label),
          noLabel: isNil(label),
          [styles.labelOutside]: labelOutside,
          [styles.withIconLeft]: iconLeft,
        },
        'labelWrapper',
      )}
    >
      {!!label && <span className={clsx(styles.label, 'label')}>{label}</span>}
      {labelOutside && !!subLabel && <div className={clsx(styles.subLabel, 'subLabel')}>{subLabel}</div>}
      <div className={clsx(styles.inputWrapper, 'inputWrapper')}>
        {iconLeft && <div className={styles.iconLeft}>{iconLeft}</div>}
        {children}
        {iconRight && <div className={styles.iconRight}>{iconRight}</div>}
        {buttonRight && <div className={styles.buttonRight}>{buttonRight}</div>}
      </div>
    </label>
    <FieldInfo id={id} error={error} note={note} errorTestId={errorTestId} />
  </div>
);

export const FieldWrapperOptionsGroup: FC<GroupProps> = ({
  id,
  label,
  subLabel,
  type,
  error,
  note,
  className,
  hasTwoColumnsLayout,
  hideError,
  noMarginBottom,
  children,
}) => (
  <fieldset
    className={clsx(styles.rootOptionsGroup, className, 'wrapper', `wrapper--${type}`, {
      [styles.rootOptionsGroup2Columns]: hasTwoColumnsLayout === 'all',
      [styles.rootOptionsGroup2ColumnsMdOnly]: hasTwoColumnsLayout === 'mdOnly',
      'mb-0': noMarginBottom,
    })}
    data-testid={id}
  >
    {!!label && (
      <>
        <legend className="sr-only">{label}</legend>
        <div className={styles.labelOptionsGroup}>
          {label}
          {!!subLabel && <div className={clsx(styles.subLabel, 'subLabel')}>{subLabel}</div>}
        </div>
      </>
    )}

    {children}
    {!hideError && <FieldInfo id={id} error={error} note={note} />}
  </fieldset>
);

const FieldInfo: FC<Pick<Props, 'id' | 'error' | 'note' | 'errorTestId'>> = ({ id, error, note, errorTestId }) => (
  <>
    {/* Field error */}
    {!!error && (
      <small
        className={clsx(styles.note, styles.error, 'note', 'error')}
        id={getInputErrorId(id)}
        data-testid={errorTestId || `${id}-enquiry-error`}
      >
        {error}
      </small>
    )}
    {/* Field note */}
    {!!note && (
      <small className={clsx(styles.note, 'note')} id={getInputNoteId(id)}>
        {note}
      </small>
    )}
  </>
);
